<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'invoices',
    path: '/invoices',
    component: 'Invoices',
    params: [
      {
        name: 'kind',
        prop: 'kind',
      },
    ],
  });
  addRoute({
    name: 'invoicesIn',
    path: '/invoices-in',
    component: 'Invoices',
    componentProps: [
      {
        name: 'kind',
        value: 'incoming',
      },
    ],
    title: () => pgettext('Invoices', 'Vendor invoices'),
  });
  addRoute({
    name: 'invoicesOut',
    path: '/invoices-out',
    component: 'Invoices',
    componentProps: [
      {
        name: 'kind',
        value: 'outgoing',
      },
    ],
    title: () => pgettext('Invoices', 'Customer invoices'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Invoices',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    kind: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getItems = (...args) => {
    const parameters = params.value;
    return parameters ? $fn.value.fetch('list_invoice', { parameters }) : [];
  };
  const invoiceTypeBadgeClass = (...args) => {
    const [row] = args;
    const cc = $config.value.invoice.types.colorCodes[row.invoice_type];
    return `bg-${cc.bg} text-${cc.text}`;
  };
  const onPrintInvoicesPayload = (...args) => {
    return $imodal.value.show({ name: 'PrintInvoices' }).then((result) => {
      if (!result || result.cancel || !result.value) {
        return;
      }
      return selectedItems.value.map((p) => ({
        invoice_id: p.id,
        copies: result.copies,
        printer: result.printer ? result.printer.id : null,
      }));
    });
  };

  const titles = ref({
    incoming: $fn.value.pgettext('Invoices', 'Vendor invoices'),
    outgoing: $fn.value.pgettext('Invoices', 'Customer invoices'),
  });
  const selectedItems = ref([]);
  const params = ref(null);
  const gridName = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_headerShowReloadButton = computed(() => false);
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
    selectable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'company',
      name: $fn.value.pgettext('Invoices', 'Company'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'invoice_number',
      name: $fn.value.pgettext('Invoices', 'Invoice nr'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'template',
              engine: 'hb',
              format:
                '#!{{ $value }} {{~#if reference_number }} ({{ reference_number }}){{~/if }}',
            },
            {
              name: 'routeLink',
              to: 'invoice',
              params: {
                id: 'id',
              },
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
                'bg-yellow-500 text-yellow-800': row.is_corrected,
                'bg-red-300 text-red-800': row.is_reversed,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'data_reporting_status',
      name: $fn.value.pgettext('Invoices', 'NAV'),
      type: 'command',
      formatter: {
        name: 'template',
        engine: 'hb',
        format:
          '<span>\n\
  {{~#if $value }}\n\
    {{~#if (eq $value.status "done") }}\n\
      <iconify-icon icon="fluent:cloud-sync-complete-16-regular" class="text-green-500">\n\
      </iconify-icon>\n\
    {{~else }}\n\
      {{~#if $value.status }}\n\
        <iconify-icon icon="fluent:warning-12-filled" class="text-yellow-500" title="{{ $value.status }}">\n\
        </iconify-icon>\n\
      {{~/if }}\n\
    {{~/if }}\n\
\n\
    {{~#if $value.message }}\n\
      <iconify-icon icon="fluent:warning-12-filled" class="text-yellow-500" title="{{ $value.message }}">\n\
      </iconify-icon>\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
      },
    },
    {
      field: 'invoice_type',
      name: $fn.value.pgettext('Invoices', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'InvoiceType',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            dot: true,
            pill: true,
            badgeClass: invoiceTypeBadgeClass,
          },
        ],
      },
    },
    {
      field: 'invoice_status',
      name: $fn.value.pgettext('Invoices', 'Status'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'Status',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantmap: $config.value.invoice.invoiceStatusVariants,
            variantMapSource: 'fieldValue',
          },
        ],
      },
    },
    {
      field: 'printout',
      name: $fn.value.pgettext('Invoices', 'Printout'),
      type: 'command',
      formatter: {
        name: 'attachment',
      },
    },
    {
      field: 'attachments',
      name: $fn.value.pgettext('Invoices', 'Attachments'),
      type: 'command',
      formatter: {
        name: 'attachment',
      },
    },
    {
      field: 'register_entry',
      name: $fn.value.pgettext('Invoices', 'Register entry'),
      type: 'command',
      params: {
        sortPath: 'register_entry.entry_number',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'entry_number',
              source: 'value',
            },
          },
          {
            name: 'link',
            href: '#',
          },
          {
            name: 'cellclass',
            value: (value) => ({
              'fw-bold': true,
              'bg-yellow-200': value && !value.locked,
            }),
          },
        ],
      },
    },
    {
      field: 'vat_period',
      name: $fn.value.pgettext('Invoices', 'VAT period'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'template',
              engine: 'hb',
              format: '#!{{ $value.name }}',
            },
          ],
        },
      ],
    },
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('Invoices', 'Date'),
    },
    {
      field: 'fulfillment_date',
      name: $fn.value.pgettext('Invoices', 'Fulfillment date'),
      type: 'date',
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('Invoices', 'Due date'),
      type: 'date',
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('Invoices', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('Invoices', 'Description'),
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('Invoices', 'Net'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'vat_amount',
      name: $fn.value.pgettext('Invoices', 'VAT'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'total_amount',
      name: $fn.value.pgettext('Invoices', 'Total'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'outstanding_amounts',
      name: $fn.value.pgettext('Invoices', 'Outstanding Amounts'),
      type: 'number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'number',
          },
          {
            name: 'cellclass',
            value: (row) => {
              if (row.flags && row.flags.no_payment_required) {
                return 'fw-bold text-decoration-line-through';
              }

              if (
                row.outstanding_amounts != null &&
                row.outstanding_amounts > 0
              ) {
                return 'fw-bold text-red-500';
              }

              if (
                row.outstanding_amounts != null &&
                row.outstanding_amounts < 0
              ) {
                return 'fw-bold text-blue-500';
              }

              if (row.outstanding_amounts == 0) {
                return 'fw-bold text-green-500';
              }
            },
          },
        ],
      },
    },
    {
      field: 'paid_amount',
      name: $fn.value.pgettext('Invoices', 'Paid'),
      type: 'number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'number',
          },
          {
            name: 'cellclass',
            value: (row) => {
              if (row.flags && row.flags.no_payment_required) {
                return 'fw-bold text-decoration-line-through';
              }
            },
          },
        ],
      },
    },
    {
      field: 'max_payment_date',
      name: $fn.value.pgettext('PaymentBrowserView', 'Date of last settlement'),
      type: 'date',
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('Invoices', 'Currency'),
    },
    {
      field: 'vat_deductible',
      if: props.kind === 'incoming',
      name: $fn.value.pgettext('InvoicesView', 'VAT deductible'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'tags',
      name: $fn.value.pgettext('Invoices', 'Tags'),
    },
    {
      field: 'rentals',
      name: $fn.value.pgettext('Invoices', 'Rentals'),
      params: {
        sortPath: 'rentals.0.name',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'contracts',
      name: $fn.value.pgettext('Invoices', 'Contracts'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    titles: titles.value,
    selectedItems: selectedItems.value,
    params: params.value,
    gridName: gridName.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_headerShowReloadButton:
      b_2248226175642056_headerShowReloadButton.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_gridName = async () => {
    return 'invoices.' + props.kind;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'titles' },
      { name: 'selectedItems' },
      { name: 'params' },
      { name: 'gridName' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_2248226175642056_headerShowReloadButton',
        nodeId: '2248226175642056',
      },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { gridName: $$load_gridName },
    ux: {},
    dataRefs: {
      titles,
      selectedItems,
      params,
      gridName,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_headerShowReloadButton,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: titles.value[props.kind],
        icon: $config.value.invoice.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.kind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('kind');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0oxi9gynf_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_caght767r_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_script_12tixhsgg_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_2248226175642056_script_12tixhsgg_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_12tixhsgg_value_callable($event);
      },
    };
  };
  const act_2248226175642056_showModal_1zv1ei73x_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        oid: $event.data.data.register_entry.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_l2ujj5wy7_getWhen = async (
    $event
  ) => {
    return (
      $event.data.column.id === 'register_entry' &&
      $event.data.data.register_entry != null
    );
  };
  const act_2248226175642056_script_ryioq41fr_value_callable = async (
    $event
  ) => {
    return $app.value.attachments.downloadAttachmentFromSlickCommand($event);
  };
  const act_2248226175642056_script_ryioq41fr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_ryioq41fr_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_command_lbtfv16fw_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'attachments';
  };
  const act_2248226175642056_script_wnmt7n50l_value_callable = async (
    $event
  ) => {
    return $app.value.attachments.downloadInvoiceView(
      $event.data.data.id,
      $event.data.data.invoice_number
    );
  };
  const act_2248226175642056_script_wnmt7n50l_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_wnmt7n50l_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_command_wnt99hius_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'printout';
  };
  const act_4615685607825208_setData_n7z6pvzph_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'params',
      value: { ...$event.data, kind: props.kind },
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_script_8t8m5cyh1_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_4615685607825208_script_8t8m5cyh1_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4615685607825208_script_8t8m5cyh1_value_callable($event);
      },
    };
  };
  const act_1269646916756998_showModal_deipecn9c_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        invoiceKind: props.kind,
      },
    };
  };
  const act_7439928539205587_showModal_05xsz6n4y_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ImportInvoices',
    };
  };
  const act_31429721812613_reloadSlickgrid_d00uu5ua9_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_31429721812613_close_cmggwbct9_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_31429721812613_showModal_lbdzdwwo5_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'SetInvoiceVatPeriod',
      props: {
        invoices: selectedItems.value,
      },
    };
  };

  const act_7315092382398562_closeModal_0oxi9gynf =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0oxi9gynf_getActionArgs,
    });
  const inst_evh_7315092382398562_close_7sz0adhuq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0oxi9gynf,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_7sz0adhuq],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_d90ro7kpd =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_mny6t76vl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_d90ro7kpd,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_mny6t76vl],
    event: 'reload',
  });
  const act_2248226175642056_setData_caght767r = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_caght767r_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_90thjoio9 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_caght767r,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_90thjoio9],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_script_12tixhsgg = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_12tixhsgg_getActionArgs,
  });
  const inst_evh_2248226175642056_saved_1irwin5dz =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_12tixhsgg,
      event: 'saved',
      displayName: 'script',
    });
  const act_2248226175642056_showModal_1zv1ei73x =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_1zv1ei73x_getActionArgs,
      events: [inst_evh_2248226175642056_saved_1irwin5dz],
    });
  const inst_evh_2248226175642056_command_l2ujj5wy7 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_1zv1ei73x,
      event: 'command',
      when: inst_evh_2248226175642056_command_l2ujj5wy7_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_script_ryioq41fr = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_ryioq41fr_getActionArgs,
  });
  const inst_evh_2248226175642056_command_lbtfv16fw =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_ryioq41fr,
      event: 'command',
      when: inst_evh_2248226175642056_command_lbtfv16fw_getWhen,
      displayName: 'script',
    });
  const act_2248226175642056_script_wnmt7n50l = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_wnmt7n50l_getActionArgs,
  });
  const inst_evh_2248226175642056_command_wnt99hius =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_wnmt7n50l,
      event: 'command',
      when: inst_evh_2248226175642056_command_wnt99hius_getWhen,
      displayName: 'script',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_l2ujj5wy7,
      inst_evh_2248226175642056_command_lbtfv16fw,
      inst_evh_2248226175642056_command_wnt99hius,
    ],
    event: 'command',
  });
  const act_4615685607825208_setData_n7z6pvzph = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_n7z6pvzph_getActionArgs,
  });
  const inst_evh_4615685607825208_submit_xyf2mrn3g =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_n7z6pvzph,
      event: 'submit',
      displayName: 'setData',
    });
  const act_4615685607825208_script_8t8m5cyh1 = new actionTypes.ScriptAction({
    actionArgs: act_4615685607825208_script_8t8m5cyh1_getActionArgs,
  });
  const inst_evh_4615685607825208_submit_qhw7x07sd =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_script_8t8m5cyh1,
      event: 'submit',
      displayName: 'script',
    });
  const inst_evh_4615685607825208_submit = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_4615685607825208_submit_xyf2mrn3g,
      inst_evh_4615685607825208_submit_qhw7x07sd,
    ],
    event: 'submit',
  });
  const act_1269646916756998_showModal_deipecn9c =
    new actionTypes.ShowModalAction({
      actionArgs: act_1269646916756998_showModal_deipecn9c_getActionArgs,
    });
  const inst_evh_1269646916756998_click_28c6qrdsy =
    new actionTypes.EventHandlerImpl({
      action: act_1269646916756998_showModal_deipecn9c,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_1269646916756998_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1269646916756998_click_28c6qrdsy],
    event: 'click',
  });
  const act_7439928539205587_showModal_05xsz6n4y =
    new actionTypes.ShowModalAction({
      actionArgs: act_7439928539205587_showModal_05xsz6n4y_getActionArgs,
    });
  const inst_evh_7439928539205587_clickPrevent_59u5dscmv =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_showModal_05xsz6n4y,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7439928539205587_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7439928539205587_clickPrevent_59u5dscmv],
      event: 'click.prevent',
    });
  const act_31429721812613_reloadSlickgrid_d00uu5ua9 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_31429721812613_reloadSlickgrid_d00uu5ua9_getActionArgs,
    });
  const inst_evh_31429721812613_close_cmggwbct9 =
    new actionTypes.EventHandlerImpl({
      action: act_31429721812613_reloadSlickgrid_d00uu5ua9,
      event: 'close',
      when: inst_evh_31429721812613_close_cmggwbct9_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_31429721812613_showModal_lbdzdwwo5 =
    new actionTypes.ShowModalAction({
      actionArgs: act_31429721812613_showModal_lbdzdwwo5_getActionArgs,
      events: [inst_evh_31429721812613_close_cmggwbct9],
    });
  const inst_evh_31429721812613_clickPrevent_skxp7n05w =
    new actionTypes.EventHandlerImpl({
      action: act_31429721812613_showModal_lbdzdwwo5,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_31429721812613_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_31429721812613_clickPrevent_skxp7n05w],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_close_7sz0adhuq,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_mny6t76vl,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_90thjoio9,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_saved_1irwin5dz,
    inst_evh_2248226175642056_command_l2ujj5wy7,
    inst_evh_2248226175642056_command_lbtfv16fw,
    inst_evh_2248226175642056_command_wnt99hius,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_submit_xyf2mrn3g,
    inst_evh_4615685607825208_submit_qhw7x07sd,
    inst_evh_4615685607825208_submit,
    inst_evh_1269646916756998_click_28c6qrdsy,
    inst_evh_1269646916756998_click,
    inst_evh_7439928539205587_clickPrevent_59u5dscmv,
    inst_evh_7439928539205587_clickPrevent,
    inst_evh_31429721812613_close_cmggwbct9,
    inst_evh_31429721812613_clickPrevent_skxp7n05w,
    inst_evh_31429721812613_clickPrevent,
  };

  const evh_7315092382398562_close_7sz0adhuq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_7sz0adhuq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_mny6t76vl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_mny6t76vl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_90thjoio9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_90thjoio9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_1irwin5dz = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_1irwin5dz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_l2ujj5wy7 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_l2ujj5wy7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_lbtfv16fw = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_lbtfv16fw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_wnt99hius = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_wnt99hius.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_submit_xyf2mrn3g = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_submit_xyf2mrn3g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_submit_qhw7x07sd = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_submit_qhw7x07sd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_submit = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1269646916756998_click_28c6qrdsy = async (event, scope) => {
    return await actions.inst_evh_1269646916756998_click_28c6qrdsy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1269646916756998_click = async (event, scope) => {
    return await actions.inst_evh_1269646916756998_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPrevent_59u5dscmv = async (event, scope) => {
    return await actions.inst_evh_7439928539205587_clickPrevent_59u5dscmv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7439928539205587_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_31429721812613_close_cmggwbct9 = async (event, scope) => {
    return await actions.inst_evh_31429721812613_close_cmggwbct9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_31429721812613_clickPrevent_skxp7n05w = async (event, scope) => {
    return await actions.inst_evh_31429721812613_clickPrevent_skxp7n05w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_31429721812613_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_31429721812613_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Invoices"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      :name="gridName"
      :headerTitle="titles[kind]"
      :items="getItems"
      :headerShowReloadButton="b_2248226175642056_headerShowReloadButton"
      checkbox-row-selection
      footer-totals
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'invoices')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <ItpBox alignItems="center" :gap="2">
          <InvoicesFilters
            :kind="kind"
            @submit="evh_4615685607825208_submit($event, {})"
          >
          </InvoicesFilters>
          <Vr class="mx-2"> </Vr>
          <BDropdown
            :text="gettext('Commands')"
            variant="light"
            class="border rounded"
          >
            <ItpButton
              icon="fa-plus"
              :text="pgettext('Invoices', 'New Invoice...')"
              dropdown-item
              @click="evh_1269646916756998_click($event, {})"
            >
            </ItpButton>
            <ItpButton
              :text="pgettext('Invoices', 'Import Invoices...')"
              :icon="$config.uploadIcon"
              dropdown-item
              v-if="kind === 'incoming'"
              @click.prevent="evh_7439928539205587_clickPrevent($event, {})"
            >
            </ItpButton>
            <ItpButton
              :text="pgettext('Invoices', 'Set VAT period...')"
              :icon="$config.vatPeriod.icon"
              :disabled="!selectedItems || !selectedItems.length"
              dropdown-item
              v-if="kind === 'incoming'"
              @click.prevent="evh_31429721812613_clickPrevent($event, {})"
            >
            </ItpButton>
            <ArtifactJob
              icon="ic:baseline-download"
              :text="gettext('Download views')"
              :disabled="!selectedItems || !selectedItems.length"
              operation="download_invoice_views"
              :payload="() => ({ identities: selectedItems.map((p) => p.id) })"
              dropdown-item
            >
            </ArtifactJob>
          </BDropdown>
          <JobHandlerControl
            jobType="invoice_print"
            load-current
            :text="pgettext('Invoices', 'Print invoices...')"
            icon="icon-park-outline:printer-one"
            :payload="onPrintInvoicesPayload"
            :disabled="!selectedItems || !selectedItems.length"
          >
          </JobHandlerControl>
        </ItpBox>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
